import React, { createContext, useContext, useEffect, useState } from "react";
import { apiUrl } from "../utils/api";
import { useAuth } from "./AuthContext";

export interface ReferenceDataUser {
    id: string;
    name: string;
    /** 'FO' | 'BO' — only set for entries from the `users` list. */
    department?: string;
    /** 'admin' | 'gfgl' | 'mitarbeiter' | 'service' — only for `users` entries. */
    role?: string;
    /** KMVS-Kürzel (`user.username`, z. B. „CLL") — für die FO-Kreise. */
    kuerzel?: string;
}

interface ReferenceDataContextValue {
    foTeam: ReferenceDataUser[];
    boTeam: ReferenceDataUser[];
    /** RBAC AppTeams (id = uuid string) for the Kontaktverantwortlicher-BO dropdown. */
    appTeams: ReferenceDataUser[];
    /** Tenant users (id = "user" table id) for the Kontaktverantwortlicher-FO dropdown. */
    users: ReferenceDataUser[];
    loading: boolean;
    refresh: () => Promise<void>;
}

const ReferenceDataContext = createContext<ReferenceDataContextValue | undefined>(
    undefined
);

export const useReferenceData = () => {
    const context = useContext(ReferenceDataContext);
    if (!context) {
        throw new Error(
            "useReferenceData must be used within a ReferenceDataProvider"
        );
    }
    return context;
};

/**
 * Cache key shapes:
 *   refData_foTeam      -> JSON-stringified ReferenceDataUser[]
 *   refData_foTeam_ts   -> ISO timestamp string of when the cache was written
 *   refData_boTeam / refData_boTeam_ts likewise.
 *
 * Hardening note (4sight audit F-14, 30.04.2026): the previous version
 * cached forever and used the cached values even if they were months old.
 * That kept ex-employees visible in the BO/FO selects long after they left
 * and exposed staff names to anyone with localStorage access. The cache
 * now expires after 6 hours; on signOut the AuthContext clears these keys
 * explicitly.
 */
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h
const KEY_FO = "refData_foTeam";
const KEY_FO_TS = "refData_foTeam_ts";
const KEY_BO = "refData_boTeam";
const KEY_BO_TS = "refData_boTeam_ts";
const KEY_APP_TEAMS = "refData_appTeams";
const KEY_APP_TEAMS_TS = "refData_appTeams_ts";
const KEY_USERS = "refData_users";
const KEY_USERS_TS = "refData_users_ts";

function readCachedTeam(dataKey: string, tsKey: string): ReferenceDataUser[] | null {
    try {
        const ts = localStorage.getItem(tsKey);
        if (!ts) return null;
        const writtenAt = new Date(ts).getTime();
        if (!Number.isFinite(writtenAt)) return null;
        if (Date.now() - writtenAt > CACHE_TTL_MS) {
            // Expired — drop it so we never accidentally read stale staff data.
            localStorage.removeItem(dataKey);
            localStorage.removeItem(tsKey);
            return null;
        }
        const raw = localStorage.getItem(dataKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function writeCachedTeam(dataKey: string, tsKey: string, value: ReferenceDataUser[]): void {
    try {
        localStorage.setItem(dataKey, JSON.stringify(value));
        localStorage.setItem(tsKey, new Date().toISOString());
    } catch { /* localStorage full / SecurityError — ignore */ }
}

export const ReferenceDataProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [foTeam, setFoTeam] = useState<ReferenceDataUser[]>([]);
    const [boTeam, setBoTeam] = useState<ReferenceDataUser[]>([]);
    const [appTeams, setAppTeams] = useState<ReferenceDataUser[]>([]);
    const [users, setUsers] = useState<ReferenceDataUser[]>([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated } = useAuth();

    const fetchData = async () => {
        setLoading(true);
        try {
            let newFoTeam: ReferenceDataUser[] = readCachedTeam(KEY_FO, KEY_FO_TS) ?? [];
            let newBoTeam: ReferenceDataUser[] = readCachedTeam(KEY_BO, KEY_BO_TS) ?? [];
            let newAppTeams: ReferenceDataUser[] = readCachedTeam(KEY_APP_TEAMS, KEY_APP_TEAMS_TS) ?? [];
            let newUsers: ReferenceDataUser[] = readCachedTeam(KEY_USERS, KEY_USERS_TS) ?? [];

            const mapManagers = (data: any): ReferenceDataUser[] => {
                if (!Array.isArray(data)) return [];
                return data.map((m: any) => ({
                    id: String(m?.id ?? ""),
                    name: m?.displayName ?? m?.lastname ?? m?.name ?? "",
                })).filter((m: ReferenceDataUser) => m.id && m.name);
            };

            // AppTeams: { id (uuid), name }. Keep ids as strings — never Number().
            const mapAppTeams = (data: any): ReferenceDataUser[] => {
                if (!Array.isArray(data)) return [];
                return data.map((t: any) => ({
                    id: String(t?.id ?? ""),
                    name: t?.name ?? "",
                })).filter((t: ReferenceDataUser) => t.id && t.name);
            };

            // Users from the "user" table picker endpoint. id = foUserId space.
            const mapUsers = (data: any): ReferenceDataUser[] => {
                if (!Array.isArray(data)) return [];
                return data.map((u: any) => {
                    const name =
                        [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim() ||
                        u?.name ||
                        u?.email ||
                        "";
                    return {
                        id: String(u?.id ?? ""),
                        name,
                        department: u?.department ?? undefined,
                        role: u?.role ?? undefined,
                        kuerzel: typeof u?.username === "string" && u.username.trim() ? u.username.trim() : undefined,
                    };
                }).filter((u: ReferenceDataUser) => u.id && u.name);
            };

            // Always try fresh fetch; fallback to cache on failure.
            try {
                const [foRes, boRes, teamsRes, usersRes] = await Promise.all([
                    fetch(apiUrl("contactgroup/team-fo")),
                    fetch(apiUrl("contactgroup/team-bo")),
                    fetch(apiUrl("rbac/teams-lite")),
                    fetch(apiUrl("users")),
                ]);

                if (foRes.ok) {
                    const foData = await foRes.json();
                    const freshFo = mapManagers(foData);
                    if (freshFo.length > 0) {
                        newFoTeam = freshFo;
                        writeCachedTeam(KEY_FO, KEY_FO_TS, newFoTeam);
                    }
                }

                if (boRes.ok) {
                    const boData = await boRes.json();
                    const freshBo = mapManagers(boData);
                    if (freshBo.length > 0) {
                        newBoTeam = freshBo;
                        writeCachedTeam(KEY_BO, KEY_BO_TS, newBoTeam);
                    }
                }

                if (teamsRes.ok) {
                    const freshTeams = mapAppTeams(await teamsRes.json());
                    if (freshTeams.length > 0) {
                        newAppTeams = freshTeams;
                        writeCachedTeam(KEY_APP_TEAMS, KEY_APP_TEAMS_TS, newAppTeams);
                    }
                }

                if (usersRes.ok) {
                    const freshUsers = mapUsers(await usersRes.json());
                    if (freshUsers.length > 0) {
                        newUsers = freshUsers;
                        writeCachedTeam(KEY_USERS, KEY_USERS_TS, newUsers);
                    }
                }
            } catch (e) {
                console.error("Failed to fetch reference data", e);
            }

            setFoTeam(newFoTeam);
            setBoTeam(newBoTeam);
            setAppTeams(newAppTeams);
            setUsers(newUsers);
        } catch (err) {
            console.error("Error in ReferenceDataProvider", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // M2: erst nach Login laden — sonst feuern die 4 Boot-Fetches auch vor/ohne Auth.
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    const refresh = async () => {
        // Clear cache + timestamps to force reload.
        localStorage.removeItem(KEY_FO);
        localStorage.removeItem(KEY_FO_TS);
        localStorage.removeItem(KEY_BO);
        localStorage.removeItem(KEY_BO_TS);
        localStorage.removeItem(KEY_APP_TEAMS);
        localStorage.removeItem(KEY_APP_TEAMS_TS);
        localStorage.removeItem(KEY_USERS);
        localStorage.removeItem(KEY_USERS_TS);
        await fetchData();
    };

    return (
        <ReferenceDataContext.Provider value={{ foTeam, boTeam, appTeams, users, loading, refresh }}>
            {children}
        </ReferenceDataContext.Provider>
    );
};
